/**
 * @namespace GroupFilter
 * @memberof Promega.Components
 * @description Methods for controlling the group filter page.
 */

import isAnimationSupported from '../util/isAnimationSupported';
import runJS from '../util/runJS';
import * as Search from '../shell/search';

const FILTER_UPDATE_DELAY = 1000,
    RESULTS_PER_REQUEST = 15,
    MINIMUM_LOADING_DELAY = 0,
    GROUP_ID_CLASS = "group__id", 
    PRODUCT_TILE_CLASS = "product-tile",
    PRODUCT_TILE_ENTERING_CLASS = "product-tile--entering",
    PRODUCT_TILE_EXITING_CLASS = "product-tile--exiting",
    PRODUCT_TILE_DIMMING_CLASS = "product-tile--dimming",
    TILE_GRID_CLASS = "grid__span-6--nucleus grid__span-4--cell",
    TILE_HEADING_CLASS = "product-tile__heading",
    TILE_CONTENT_CLASS = "product-tile__content",
    TILE_PART_NUM_CLASS  = "product-tile__part-number",
    RESULT_CONTAINER_CLASS = "group__result-container",
    RESULT_CONTAINER_EXITING_CLASS = "group__result-container--exiting",
    CHECKBOX_CLASS = "group__filter-checkbox",
    FILTER_COUNT_CLASS = "group__filter-count",
    FILTERED_COUNT_CLASS = "group__filtered-total",
    CURRENT_COUNT_CLASS = "group__current-count",
    VIEW_MORE_BUTTON_CLASS = "group__more-results-button",
    VIEW_MORE_BUTTON_VISIBLE_CLASS = "group__more-results-button--visible",
    LOADING_CLASS = "loading-indicator--group-filter",
    LOADING_VISIBLE_CLASS = "loading-indicator--visible",
    CLEAR_FILTERS_BUTTON_CLASS = "group__clear-filters-button",
    SORT_DROPDOWN_CLASS = "group__sort-dropdown";

let totalWithFilters = null,
    groupId = null,
    viewMoreButton = null,
    clearFiltersButton = null,
    loadingIndicator = null,
    resultContainer = null,
    sortDropdown = null,
    filterDelayTimer = null,
    minimumLoadingTimer = null,
    pendingResults = null,
    replaceResultsAfterLoad = false;

// Private functions/listeners

function checkLoadingCompletion () {
    if (minimumLoadingTimer === null && pendingResults !== null) {
        // Loading is complete.
        loadingIndicator.classList.remove(LOADING_VISIBLE_CLASS);
        totalWithFilters = pendingResults.totalResults;

        if (replaceResultsAfterLoad && isAnimationSupported()) {
            // Fade the results before continuing
            resultContainer.addEventListener("animationend", resultsFinishedHiding);
            resultContainer.classList.add(RESULT_CONTAINER_EXITING_CLASS);
        }
        else {
            finishAddingResults();
        }
    }
}

function resultsFinishedHiding (event) {
    resultContainer.removeEventListener("animationend", resultsFinishedHiding);
    resultContainer.classList.remove(RESULT_CONTAINER_EXITING_CLASS);
    finishAddingResults();
}

function finishAddingResults () {
    // Append the results to the container
    if (replaceResultsAfterLoad) {
        clearResults();
        replaceResultsAfterLoad = false;
    }
    appendResults(pendingResults.results);
    updateFilterCheckboxes(pendingResults.filters);
    updateLoadMoreButtonVisibility();
    sortDropdown.removeAttribute("disabled");
    runJS(pendingResults.productImpressionsJS);

    pendingResults = null;
}
   
function updateLoadMoreButtonVisibility () {
    if (totalWithFilters > getTotalDisplayedProducts()) viewMoreButton.classList.add(VIEW_MORE_BUTTON_VISIBLE_CLASS);
    else viewMoreButton.classList.remove(VIEW_MORE_BUTTON_VISIBLE_CLASS);
}

function tileAnimationEnded (event) {
    if (event && event.target.classList.contains(PRODUCT_TILE_CLASS)) {
        event.target.classList.remove(PRODUCT_TILE_ENTERING_CLASS);
        event.target.classList.remove(PRODUCT_TILE_EXITING_CLASS);
    }
}

function dimAllTiles() {
    const allTiles = document.getElementsByClassName(PRODUCT_TILE_CLASS);
    for (let i = 0; i < allTiles.length; i++) {
        allTiles[i].classList.add(PRODUCT_TILE_DIMMING_CLASS);
        allTiles[i].style.animationDelay = "";
    }
}

function filterChanged (event) {
    if (event.target && event.target.classList.contains(CHECKBOX_CLASS)) {
        if (filterDelayTimer === null) {
            dimAllTiles();
            sortDropdown.disabled = true;
        }
        else {
            clearTimeout(filterDelayTimer);
        }

        filterDelayTimer = setTimeout(filterChangeTimedOut, FILTER_UPDATE_DELAY);
    }
}

function filterChangeTimedOut () {
    filterDelayTimer = null;
    requestInitialResults();
}

function sortDropdownChanged () {
    requestInitialResults();
}

function updateDisplayedProductCount () {
    const allCounts = document.getElementsByClassName(CURRENT_COUNT_CLASS),
        currentCount = getTotalDisplayedProducts();
    for (let i=0; i<allCounts.length; i++) {
        allCounts[i].innerText = currentCount;
    }

    const allFilteredTotals = document.getElementsByClassName(FILTERED_COUNT_CLASS);
    for (let i=0; i<allFilteredTotals.length; i++) {
        allFilteredTotals[i].innerText = totalWithFilters;
    }
}

function resultsReceived (results) {
    pendingResults = results;
    checkLoadingCompletion();
}

function initialResultsError (err) {
}

function generateDefaultRequestOptions () {
    let config = {
        index: 0,
        count: RESULTS_PER_REQUEST,
        sort: null,
        filters: []
    };

    // Add all selected filters
    const checkedFilters = document.querySelectorAll("." + CHECKBOX_CLASS + ":checked");
    for (let i=0; i<checkedFilters.length; i++) {
        config.filters.push(checkedFilters[i].getAttribute("data-id"));
    }

    // Include sort value
    if (sortDropdown && sortDropdown.value.length > 0) {
        config.sort = sortDropdown.value;
    }

    return config;
}

function loadingDelayCompleted () {
    minimumLoadingTimer = null;
    checkLoadingCompletion();
}

function disableAllFilters () {
    const allFilterCheckboxes = document.getElementsByClassName(CHECKBOX_CLASS);
    for (let i=0; i<allFilterCheckboxes.length; i++) {
        allFilterCheckboxes[i].setAttribute("disabled", "disabled");
    }
}

// API

/**
 * @function getTotalDisplayedProducts
 * @memberof Promega.Components.GroupFilter
 * @description Gets the number of products currently being displayed
 * @returns {Number}
 */
export function getTotalDisplayedProducts () {
    return document.getElementsByClassName(PRODUCT_TILE_CLASS).length;
}

/**
 * @function generateProductTile
 * @memberof Promega.Components.GroupFilter
 * @description Constructs an HTML element representing the given product.
 * @param {JSON} json A JSON object representing the product
 * @param {String} json.url
 * @param {String} json.title
 * @param {String} json.description
 * @param {Array<String>} json.catNums
 * @param {String} json.promo
 * @param {JSON} [options]
 * @param {Boolean} options.animateEntrance If true, causes the product tile to use an entrance animation if/when it is added to the DOM
 * @returns {HTMLAnchorElement} The product tile
 */
export function generateProductTile (json, options) {
    if (json) {
        const tileEl = document.createElement("a");
        tileEl.className = TILE_GRID_CLASS + " " + PRODUCT_TILE_CLASS;
        if (options && options.animateEntrance) tileEl.classList.add(PRODUCT_TILE_ENTERING_CLASS);
        tileEl.href = json.url;
        tileEl.setAttribute("onclick", json.productClickJS);

        const tileHeading = document.createElement("h4");
        tileHeading.className = TILE_HEADING_CLASS;
        tileHeading.innerHTML = json.title;
        tileEl.appendChild(tileHeading); 

        const tileContent = document.createElement("p");
        tileContent.className = TILE_CONTENT_CLASS;
        tileContent.innerHTML = json.description;
        tileEl.appendChild(tileContent);

        const tileCatNums = document.createElement("p");
        tileCatNums.className = TILE_PART_NUM_CLASS;
        let catNumString = "";
        if (json.catNums) {
            for (let i=0; i<json.catNums.length; i++) {
                if (i>0) catNumString += ", ";
                catNumString += json.catNums[i];
            }
        }
        tileCatNums.innerText = catNumString;
        tileEl.appendChild(tileCatNums);

        if (json.promo !== null && json.promo.length > 0) {
            tileEl.setAttribute("data-promo-text", json.promo);
        }                
                
        return tileEl;
    }
}

/**
 * @function requestMoreResults
 * @memberof Promega.Components.GroupFilter
 * @description Requests 15 additional results from the current position.
 */
export function requestMoreResults () {
    viewMoreButton.classList.remove(VIEW_MORE_BUTTON_VISIBLE_CLASS);
    loadingIndicator.classList.add(LOADING_VISIBLE_CLASS);

    let config = generateDefaultRequestOptions();
    config.index = getTotalDisplayedProducts();
    minimumLoadingTimer = setTimeout(loadingDelayCompleted, MINIMUM_LOADING_DELAY);
    disableAllFilters();
    Search.getProductsByGroup(groupId, config, resultsReceived, initialResultsError);
}

/**
 * @function requestInitialResults
 * @memberof Promega.Components.GroupFilter
 * @description Clears the displayed results, and requests a new starting set based on the filters that are currently checked.
 */
export function requestInitialResults () {
    viewMoreButton.classList.remove(VIEW_MORE_BUTTON_VISIBLE_CLASS);
    loadingIndicator.classList.add(LOADING_VISIBLE_CLASS);

    let config = generateDefaultRequestOptions();
            
    replaceResultsAfterLoad = true;
    minimumLoadingTimer = setTimeout(loadingDelayCompleted, MINIMUM_LOADING_DELAY);
    disableAllFilters();
    dimAllTiles();
    sortDropdown.disabled = true;
    Search.getProductsByGroup(groupId, config, resultsReceived, initialResultsError);
}

/**
 * @function appendResults
 * @memberof Promega.Components.GroupFilter
 * @description Appends the given array of results to the display.
 * @param {Array<JSON>} results An array of JSON objects representing product tiles
 */
export function appendResults (results) {
    // Appends the given array of results to the display
    const generationOptions = {animateEntrance: isAnimationSupported()};
    for (let i=0; i<results.length; i++) {
        const newTile = generateProductTile(results[i], generationOptions);
        if (generationOptions.animateEntrance) newTile.style.animationDelay = (i * .025) + "s";
        resultContainer.appendChild(newTile);
               
    }
    updateDisplayedProductCount();
}

/**
 * @function clearResults
 * @memberof Promega.Components.GroupFilter
 * @description Clears the list of product tiles and removes all tiles that are currently being displayed on the page.
 */
export function clearResults () {
    while (resultContainer.hasChildNodes()) resultContainer.removeChild(resultContainer.lastChild);
    updateDisplayedProductCount();
}

/**
 * @function clearFilters
 * @memberof Promega.Components.GroupFilter
 * @description Clears all filters and re-requests the initial set of unfiltered results.
 */
export function clearFilters () {
    const checkedFilters = document.querySelectorAll("." + CHECKBOX_CLASS + ":checked");
    if (checkedFilters.length > 0) {
        clearFiltersButton.hidden = true;
        for (let i=0; i<checkedFilters.length; i++) {
            checkedFilters[i].checked = false;
        }
        requestInitialResults();
    }
}

/**
 * @function updateFilterCheckboxes
 * @memberof Promega.Components.GroupFilter
 * @description Given an array of filters, enables and disables the relevant filter checkboxes on the page. Filters that have zero possible results will be disabled; filters with one or more possible results will be enabled.
 * @param {Array<{results: Number}>} filters An array of JSON objects representing the filters to update
 */
export function updateFilterCheckboxes (filters) {
    // Updates the selectability of the given filters
    const checkedFilters = document.querySelectorAll("." + CHECKBOX_CLASS + ":checked");
    const allFilterCheckboxes = document.getElementsByClassName(CHECKBOX_CLASS);
    for (let i=0; i<allFilterCheckboxes.length; i++) {
        const currentCheckbox = allFilterCheckboxes[i],
            currentFilter = filters.filter((testFilter) => currentCheckbox.getAttribute("data-id") === testFilter.id)[0];
                
        let currentCount = 0;
                
        if (currentFilter) currentCount = currentFilter.results;

        // Re-enable all checkboxes
        currentCheckbox.removeAttribute("disabled");
        //if (currentCount > 0 || currentCheckbox.checked) currentCheckbox.removeAttribute("disabled");
        //else currentCheckbox.setAttribute("disabled", "disabled");

        const labelParent = currentCheckbox.closest("label");
        if (labelParent) {
            const countEl = labelParent.getElementsByClassName(FILTER_COUNT_CLASS)[0];
            if (countEl) countEl.innerText = ' (' + currentCount + ')';
        }
    }

    clearFiltersButton.hidden = !(checkedFilters.length > 0);
}
    

// Init code

{
    const groupIdEl = document.getElementsByClassName(GROUP_ID_CLASS)[0]
    if (groupIdEl) {
        groupId = groupIdEl.value;

        document.addEventListener("change", filterChanged);

        totalWithFilters = parseInt(document.getElementsByClassName(FILTERED_COUNT_CLASS)[0].innerText, 10);

        viewMoreButton = document.getElementsByClassName(VIEW_MORE_BUTTON_CLASS)[0];
        if (viewMoreButton) {
            viewMoreButton.addEventListener("click", () => { requestMoreResults(); });
        }
        updateDisplayedProductCount();
        updateLoadMoreButtonVisibility();

        loadingIndicator = document.getElementsByClassName(LOADING_CLASS)[0];
        resultContainer = document.getElementsByClassName(RESULT_CONTAINER_CLASS)[0];
        sortDropdown = document.getElementsByClassName(SORT_DROPDOWN_CLASS)[0];

        sortDropdown.addEventListener("change", sortDropdownChanged);

        clearFiltersButton = document.getElementsByClassName(CLEAR_FILTERS_BUTTON_CLASS)[0];
        if (clearFiltersButton) clearFiltersButton.addEventListener("click", () => { clearFilters(); });

        document.addEventListener("animationend", tileAnimationEnded);

        updateFilterCheckboxes([]);

        if (groupIdEl.hasAttribute('data-autoload'))
            requestInitialResults();
    }
}
